/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  UPDATE_LISTING_CURRENT_PAGE,
  UPDATE_LISTING_PAGE_SIZE,
  UPDATE_USERPROFILE_ID,
  USERPROFILE_ADD_COMMENT,
  USERPROFILE_ADD_COMMENT_FAIL,
  USERPROFILE_ADD_COMMENT_SUCCESS,
  USERPROFILE_CLAIM_BENEFIT,
  USERPROFILE_CLAIM_BENEFIT_FAIL,
  USERPROFILE_CLAIM_BENEFIT_RESET,
  USERPROFILE_CLAIM_BENEFIT_SUCCESS,
  USERPROFILE_DL_LOAD,
  USERPROFILE_DL_LOAD_FAIL,
  USERPROFILE_DL_LOAD_SUCCESS,
  USERPROFILE_DL_REMOVE,
  USERPROFILE_DL_REMOVE_ALL,
  USERPROFILE_DL_REMOVE_ALL_FAIL,
  USERPROFILE_DL_REMOVE_ALL_SUCCESS,
  USERPROFILE_DL_REMOVE_FAIL,
  USERPROFILE_DL_REMOVE_SUCCESS,
  USERPROFILE_END_VSBENEFITS,
  USERPROFILE_END_VSBENEFITS_FAIL,
  USERPROFILE_END_VSBENEFITS_SUCCESS,
  USERPROFILE_EXTEND_VSBENEFITS,
  USERPROFILE_EXTEND_VSBENEFITS_FAIL,
  USERPROFILE_EXTEND_VSBENEFITS_SUCCESS,
  USERPROFILE_FETCH_PROGRAM_PROFILE,
  USERPROFILE_FETCH_PROGRAM_PROFILE_FAIL,
  USERPROFILE_FETCH_PROGRAM_PROFILE_SUCCESS,
  USERPROFILE_GET_PROFILE_BENEFITS,
  USERPROFILE_GET_PROFILE_BENEFITS_FAIL,
  USERPROFILE_GET_PROFILE_BENEFITS_SUCCESS,
  USERPROFILE_LOAD,
  USERPROFILE_LOAD_BY_EMAIL,
  USERPROFILE_LOAD_COMMENTS,
  USERPROFILE_LOAD_COMMENTS_FAIL,
  USERPROFILE_LOAD_COMMENTS_SUCCESS,
  USERPROFILE_LOAD_FAIL,
  USERPROFILE_LOAD_SUCCESS,
  USERPROFILE_LOAD_VSBENEFITS,
  USERPROFILE_LOAD_VSBENEFITS_FAIL,
  USERPROFILE_LOAD_VSBENEFITS_SUCCESS,
  USERPROFILE_PROVISION_VSBENEFITS,
  USERPROFILE_PROVISION_VSBENEFITS_FAIL,
  USERPROFILE_PROVISION_VSBENEFITS_SUCCESS,
  USERPROFILE_UPDATE,
  USERPROFILE_UPDATE_BLOCK_SHIPPING,
  USERPROFILE_UPDATE_BLOCK_SHIPPING_FAIL,
  USERPROFILE_UPDATE_BLOCK_SHIPPING_SUCCESS,
  USERPROFILE_UPDATE_COMMENT,
  USERPROFILE_UPDATE_COMMENT_FAIL,
  USERPROFILE_UPDATE_COMMENT_SUCCESS,
  USERPROFILE_UPDATE_FAIL,
  USERPROFILE_UPDATE_IS_PRIVATE,
  USERPROFILE_UPDATE_IS_PRIVATE_FAIL,
  USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS,
  USERPROFILE_UPDATE_PROGRAM_PROFILE,
  USERPROFILE_UPDATE_PROGRAM_PROFILE_FAILURE,
  USERPROFILE_UPDATE_PROGRAM_PROFILE_SUCCESS,
  USERPROFILE_UPDATE_SUCCESS,
  USERPROFILE_VSBENEFITS_RESET,
  USERPROFILES_FILTER_PROVINCE_VALUE_REMOVE,
  USERPROFILES_FILTER_VALUE_ADD,
  USERPROFILES_FILTER_VALUE_REMOVE,
  USERPROFILES_FILTER_VALUE_RESET,
  USERPROFILES_FILTER_VALUE_UPDATE,
  USERPROFILES_SEARCH,
  USERPROFILES_SEARCH_FAIL,
  USERPROFILES_SEARCH_SUCCESS,
} from "Constants/ActionTypes";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { IProfileBenefitResult } from "Interfaces/Benefits/IProfileBenefitResult";
import { IFilterValue } from "Interfaces/IFilterValue";
import { IProgramProfile } from "Interfaces/ProgramProfile/IProgramProfile";
import { IUserProfile } from "Interfaces/UserProfile/IUserProfile";
import { IUserProfileComment } from "Interfaces/UserProfile/IUserProfileComment";
import { IUserProfileVSBenefit } from "Interfaces/UserProfile/IUserProfileVSBenefit";
import { IUserProfileVSBenefitsUpdateRequest } from "Interfaces/UserProfile/IUserProfileVSBenefitsUpdateRequest";

/*************** BEGIN INTERFACES *************** */
/**
 * Action to get profile benefits.
 * @interface
 */
export interface IGetProfileBenefits {
  type: typeof USERPROFILE_GET_PROFILE_BENEFITS;
  userProfileId: number;
}

/**
 * Action to get profile benefits success.
 * @interface
 */
export interface IGetProfileBenefitsSuccess {
  type: typeof USERPROFILE_GET_PROFILE_BENEFITS_SUCCESS;
  userProfileBenefits: IProfileBenefitResult;
}

/**
 * Action to get profile benefits fail.
 * @interface
 */
export interface IGetProfileBenefitsFail {
  type: typeof USERPROFILE_GET_PROFILE_BENEFITS_FAIL;
  profileBenefitsErrorMessage: string;
}

/**
 * Action for when updating user profile program details.
 * @interface
 */
export interface IUpdateProgramProfile {
  type: typeof USERPROFILE_UPDATE_PROGRAM_PROFILE;
  programProfile: IProgramProfile;
}

/**
 * Action for when updating user profile Program details success.
 * @interface
 */
export interface IUpdateProgramProfileSuccess {
  type: typeof USERPROFILE_UPDATE_PROGRAM_PROFILE_SUCCESS;
  programProfile: IProgramProfile;
}

/**
 * Action for when updating user profile Program details failure.
 * @interface
 */
export interface IUpdateProgramProfileFailure {
  type: typeof USERPROFILE_UPDATE_PROGRAM_PROFILE_FAILURE;
  message: string;
}

/**
 * Action for when updating listing current page
 * @interface
 */
export interface IUpdateCurrentPage {
  type: typeof UPDATE_LISTING_CURRENT_PAGE;
  currentPage: number;
}

/**
 * Action for when updating listing page size
 * @interface
 */
export interface IUpdatePageSize {
  type: typeof UPDATE_LISTING_PAGE_SIZE;
  pageSize: number;
}

/**
 * Action for when searching user profiles.
 * @interface
 */
export interface ISearchUserProfiles {
  type: typeof USERPROFILES_SEARCH;
  searchText: string;
  selectedUserProfilesFilterValues: IFilterValue[];
  searchPage: number;
  pageSize: number;
}

/**
 * Action for when user profiles were searched successfully.
 * @interface
 */
export interface ISearchUserProfilesSuccess {
  type: typeof USERPROFILES_SEARCH_SUCCESS;
  userProfiles: IUserProfile[];
  searchPage: number;
  filteredCount: number;
  pageSize: number;
}

/**
 * Action for when searching user profiles failed.
 * @interface
 */
export interface ISearchUserProfilesFail {
  type: typeof USERPROFILES_SEARCH_FAIL;
  error: string;
}

/**
 * Action for when adding selected filter value.
 * @interface
 */
export interface IUserProfilesFilterValueAdd {
  type: typeof USERPROFILES_FILTER_VALUE_ADD;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected filter value.
 * @interface
 */
export interface IUserProfilesFilterValueRemove {
  type: typeof USERPROFILES_FILTER_VALUE_REMOVE;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected province filter value.
 * @interface
 */
export interface IUserProfilesFilterProvinceValueRemove {
  type: typeof USERPROFILES_FILTER_PROVINCE_VALUE_REMOVE;
}

/**
 * Action for when updating selected filter value.
 * @interface
 */
export interface IUserProfilesFilterValueUpdate {
  type: typeof USERPROFILES_FILTER_VALUE_UPDATE;
  filterValue: IFilterValue;
}

/**
 * Action for when loading a user profile.
 * @interface
 */
export interface ILoadUserProfile {
  type: typeof USERPROFILE_LOAD;
  userProfileId: number;
}

/**
 * action for when loading a user profile.
 * @interface
 */
export interface ILoadUserProfileByEmail {
  type: typeof USERPROFILE_LOAD_BY_EMAIL;
  email: string;
}

/**
 * Action for when user profile was loaded successfully.
 * @interface
 */
export interface ILoadUserProfileSuccess {
  type: typeof USERPROFILE_LOAD_SUCCESS;
  userProfile: IUserProfile;
}

/**
 * Action for when loading a user profile failed.
 * @interface
 */
export interface ILoadUserProfileFail {
  type: typeof USERPROFILE_LOAD_FAIL;
  error: string;
}

/**
 * Action for when updating user profile ID.
 * @interface
 */
export interface IUpdateUserProfileId {
  type: typeof UPDATE_USERPROFILE_ID;
  userProfileId: number;
}

/**
 * Action for updating a user profile.
 * @interface
 */
export interface IUpdateUserProfile {
  type: typeof USERPROFILE_UPDATE;
  userProfile: IUserProfile;
}

/**
 * Action for when updating a user profile is successful.
 * @interface
 */
export interface IUpdateUserProfileSuccess {
  type: typeof USERPROFILE_UPDATE_SUCCESS;
  userProfile: IUserProfile;
}

/**
 * Action for when updating a user profile fails.
 * @interface
 */
export interface IUpdateUserProfileFail {
  type: typeof USERPROFILE_UPDATE_FAIL;
  error: string;
}

/**
 * Action for when loading user profile comments.
 * @interface
 */
export interface ILoadUserProfileComments {
  type: typeof USERPROFILE_LOAD_COMMENTS;
  userProfileId: number;
}

/**
 * Action for when user profile comments were loaded successfully.
 * @interface
 */
export interface ILoadUserProfileCommentsSuccess {
  type: typeof USERPROFILE_LOAD_COMMENTS_SUCCESS;
  userProfileComments: IUserProfileComment[];
}

/**
 * Action for when loading user profile comments failed.
 * @interface
 */
export interface ILoadUserProfileCommentsFail {
  type: typeof USERPROFILE_LOAD_COMMENTS_FAIL;
  loadCommentsError: string;
}

/**
 * Action for when updating a user profile comment.
 * @interface
 */
export interface IUpdateUserProfileComment {
  type: typeof USERPROFILE_UPDATE_COMMENT;
  userProfileComment: IUserProfileComment;
}

/**
 * Action for when a user profile comment were updated successfully.
 * @interface
 */
export interface IUpdateUserProfileCommentSuccess {
  type: typeof USERPROFILE_UPDATE_COMMENT_SUCCESS;
  userProfileComment: IUserProfileComment;
}

/**
 * Action for when updating a user profile comment failed.
 * @interface
 */
export interface IUpdateUserProfileCommentFail {
  type: typeof USERPROFILE_UPDATE_COMMENT_FAIL;
  error: string;
}

/**
 * Action to reset the VS Benefits State.
 * @interface
 */
export interface IUserProfileVSBenefitsReset {
  type: typeof USERPROFILE_VSBENEFITS_RESET;
}

/**
 * Action for loading Visual Studio benefits for a user profile
 * @interface
 */
export interface ILoadUserProfileVSBenefits {
  type: typeof USERPROFILE_LOAD_VSBENEFITS;
  userProfileId: number;
}

/**
 * Action for when user profile Visual Studio benefits were loaded successfully.
 * @interface
 */
export interface ILoadUserProfileVSBenefitsSuccess {
  type: typeof USERPROFILE_LOAD_VSBENEFITS_SUCCESS;
  userProfileVSBenefit: IUserProfileVSBenefit;
}

/**
 * Action for when loading user profile Visual Studio benefits failed.
 * @interface
 */
export interface ILoadUserProfileVSBenefitsFail {
  type: typeof USERPROFILE_LOAD_VSBENEFITS_FAIL;
  error: string;
}

/**
 * Action for Provisioning Visual Studiobenefits for a user profile
 * @interface
 */
export interface IProvisionUserProfileVSBenefits {
  type: typeof USERPROFILE_PROVISION_VSBENEFITS;
  userProfileId: number;
}

/**
 * Action for when user profile Visual Studio benefits were provisioned successfully.
 * @interface
 */
export interface IProvisionUserProfileVSBenefitsSuccess {
  type: typeof USERPROFILE_PROVISION_VSBENEFITS_SUCCESS;
  userProfileVSBenefitRequest: IUserProfileVSBenefitsUpdateRequest;
}

/**
 * Action for when provisioning user profile Visual Studio benefits fails.
 * @interface
 */
export interface IProvisionUserProfileVSBenefitsFail {
  type: typeof USERPROFILE_PROVISION_VSBENEFITS_FAIL;
  error: string;
}

/**
 * Action for extending Visual Studio benefits for a user profile
 * @interface
 */
export interface IExtendUserProfileVSBenefits {
  type: typeof USERPROFILE_EXTEND_VSBENEFITS;
  userProfileId: number;
}

/**
 * Action for when user profile Visual Studio benefits were extended successfully.
 * @interface
 */
export interface IExtendUserProfileVSBenefitsSuccess {
  type: typeof USERPROFILE_EXTEND_VSBENEFITS_SUCCESS;
  userProfileVSBenefitRequest: IUserProfileVSBenefitsUpdateRequest;
}

/**
 * Action for when extending user profile Visual Studio benefits fails.
 * @interface
 */
export interface IExtendUserProfileVSBenefitsFail {
  type: typeof USERPROFILE_EXTEND_VSBENEFITS_FAIL;
  error: string;
}

/**
 * Action for ending Visual Studio benefits for a user profile
 * @interface
 */
export interface IEndUserProfileVSBenefits {
  type: typeof USERPROFILE_END_VSBENEFITS;
  userProfileId: number;
}

/**
 * Action for when user profile Visual Studio benefits were extended successfully.
 * @interface
 */
export interface IEndUserProfileVSBenefitsSuccess {
  type: typeof USERPROFILE_END_VSBENEFITS_SUCCESS;
  userProfileVSBenefitRequest: IUserProfileVSBenefitsUpdateRequest;
}

/**
 * Action for when ending user profile Visual Studio benefits fails.
 * @interface
 */
export interface IEndUserProfileVSBenefitsFail {
  type: typeof USERPROFILE_END_VSBENEFITS_FAIL;
  error: string;
}

/**
 * Action claiming the benefits for a user profile.
 * @interface
 */
export interface IClaimBenefit {
  type: typeof USERPROFILE_CLAIM_BENEFIT;
  userProfileId: number;
  benefitType: string;
  tenant: string;
}

/**
 * Action claiming the benefits for a user profile succeed.
 * @interface
 */
export interface IClaimBenefitSuccess {
  type: typeof USERPROFILE_CLAIM_BENEFIT_SUCCESS;
}

/**
 * Action claiming the benefits for a user profile fail.
 * @interface
 */
export interface IClaimBenefitFail {
  type: typeof USERPROFILE_CLAIM_BENEFIT_FAIL;
  error: string;
}

/**
 * Action claiming the benefits for a user profile reset.
 * @interface
 */
export interface IClaimBenefitReset {
  type: typeof USERPROFILE_CLAIM_BENEFIT_RESET;
}

export interface IFetchProgramProfile {
  type: typeof USERPROFILE_FETCH_PROGRAM_PROFILE;
  programProfileId: number;
}

/**
 * Action to fetch Program Profile fail
 * @interface
 */
export interface IFetchProgramProfileFail {
  type: typeof USERPROFILE_FETCH_PROGRAM_PROFILE_FAIL;
  errorMessage: string;
}

/**
 * Action to fetch Program Profile success
 * @interface
 */
export interface IFetchProgramProfileSuccess {
  type: typeof USERPROFILE_FETCH_PROGRAM_PROFILE_SUCCESS;
  programProfile: IProgramProfile;
}

/**
 * Action to add new comment
 * @interface
 */
export interface IAddNewComment {
  type: typeof USERPROFILE_ADD_COMMENT;
  userProfileId: number;
  newComment: string;
  createdBy: string;
}

/**
 * Action: add new comment success
 * @interface
 */
export interface IAddNewCommentSuccess {
  type: typeof USERPROFILE_ADD_COMMENT_SUCCESS;
  userProfileId: number;
}

/**
 * Action: add new comment fail
 * @interface
 */
export interface IAddNewCommentFail {
  type: typeof USERPROFILE_ADD_COMMENT_FAIL;
  errorMessage: string;
}

/**
 * Action: update user's blocked shipping status
 * @interface
 */
export interface IUserProfileUpdateBlockShipping {
  type: typeof USERPROFILE_UPDATE_BLOCK_SHIPPING;
  isShippingBlocked: boolean;
  userProfileId: number;
}

/**
 * Action: update user's blocked shipping status fail
 * @interface
 */
export interface IUserProfileUpdateBlockShippingFail {
  type: typeof USERPROFILE_UPDATE_BLOCK_SHIPPING_FAIL;
  errorMessage: string;
}

/**
 * Action: update user's blocked shipping status success
 * @interface
 */
export interface IUserProfileUpdateBlockShippingSuccess {
  type: typeof USERPROFILE_UPDATE_BLOCK_SHIPPING_SUCCESS;
  userProfileId: number;
}

/**
 * Action: load the profile's DL.
 * @interface
 */
export interface IUserProfileDlLoad {
  type: typeof USERPROFILE_DL_LOAD;
  userProfileId: number;
  tenant: string;
}

/**
 * Action: load the profile's DL succeed.
 * @interface
 */
export interface IUserProfileDlLoadSuccess {
  type: typeof USERPROFILE_DL_LOAD_SUCCESS;
  distributionLists: any;
}

/**
 * Action: load the profile's DL fail.
 * @interface
 */
export interface IUserProfileDlLoadFail {
  type: typeof USERPROFILE_DL_LOAD_FAIL;
  error: string;
}

/**
 * Action: remove the profile's from DL.
 * @interface
 */
export interface IUserProfileDlRemove {
  type: typeof USERPROFILE_DL_REMOVE;
  dlGroup: string;
  userEmail: string;
  tenant: string;
}

/**
 * Action: remove the profile's from DL succeed.
 * @interface
 */
export interface IUserProfileDlRemoveSuccess {
  type: typeof USERPROFILE_DL_REMOVE_SUCCESS;
}

/**
 * Action: remove the profile's from DL fail.
 * @interface
 */
export interface IUserProfileDlRemoveFail {
  type: typeof USERPROFILE_DL_REMOVE_FAIL;
  error: string;
}

/**
 * Action: remove the profile's from all DL.
 * @interface
 */
export interface IUserProfileDlRemoveAll {
  type: typeof USERPROFILE_DL_REMOVE_ALL;
  userProfileId: number;
  tenant: string;
}

/**
 * Action: remove the profile's from all DL succeed.
 * @interface
 */
export interface IUserProfileDlRemoveAllSuccess {
  type: typeof USERPROFILE_DL_REMOVE_ALL_SUCCESS;
}

/**
 * Action: remove the profile's from all DL fail.
 * @interface
 */
export interface IUserProfileDlRemoveAllFail {
  type: typeof USERPROFILE_DL_REMOVE_ALL_FAIL;
  error: string;
}

/**
 * Action: update user's profile is private flag.
 * @interface
 */
export interface IUserProfileIsPrivateUpdate {
  type: typeof USERPROFILE_UPDATE_IS_PRIVATE;
  isPrivate: boolean;
  userProfileId: number;
}

/**
 * Action: update user's profile is private flag success.
 * @interface
 */
export interface IUserProfileIsPrivateUpdateSuccess {
  type: typeof USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS;
}

/**
 * Action: update user's profile is private flag fail.
 * @interface
 */
export interface IUserProfileIsPrivateUpdateFail {
  type: typeof USERPROFILE_UPDATE_IS_PRIVATE_FAIL;
  error: string;
}

/**
 * Action: reset user profile.
 */
export interface IUserProfileFilterValueReset {
  type: typeof USERPROFILES_FILTER_VALUE_RESET;
}

/*************** END INTERFACES *************** */

/*************** BEGIN FUNCTIONS *************** */

/**
 * Action used for getting profile benefits.
 * @param userProfileId the userProfileId to get benefits for.
 * @param tenant the tenant to get benefits for.
 * @returns {IGetProfileBenefits} the action.
 */
export function getProfileBenefits(userProfileId: number): UserProfileActionTypes {
  return {
    type: USERPROFILE_GET_PROFILE_BENEFITS,
    userProfileId,
  };
}

/**
 * Action used for getting profile benefits success.
 * @param userProfileBenefits the benefits for the profile.
 * @returns {IGetProfileBenefitsSuccess} the action.
 */
export function getProfileBenefitsSuccess(
  userProfileBenefits: IProfileBenefitResult
): UserProfileActionTypes {
  return {
    type: USERPROFILE_GET_PROFILE_BENEFITS_SUCCESS,
    userProfileBenefits,
  };
}

/**
 * Action used for getting profile benefits fail.
 * @param profileBenefitsErrorMessage the profileBenefitsError message to set
 * @returns {IGetProfileBenefitsFail} the action.
 */
export function profileBenefitsFail(profileBenefitsErrorMessage: string): UserProfileActionTypes {
  return {
    type: USERPROFILE_GET_PROFILE_BENEFITS_FAIL,
    profileBenefitsErrorMessage,
  };
}

/**
 * Action creator for the update program profile failure.
 * @param {string} message the error message returned
 * @returns {IUpdateProgramProfileFailure} the update program profile failure action.
 */
export function updateProgramProfileFailure(message: string): UserProfileActionTypes {
  return {
    type: USERPROFILE_UPDATE_PROGRAM_PROFILE_FAILURE,
    message,
  };
}
/**
 * Action creator for the Update ProgramProfile success.
 * @param {IProgramProfile} programProfile the updated program profile details.
 * @returns {IUpdateProgramProfileSuccess} the update program profile success action.
 */
export function updateProgramProfileSuccess(
  programProfile: IProgramProfile
): UserProfileActionTypes {
  return {
    type: USERPROFILE_UPDATE_PROGRAM_PROFILE_SUCCESS,
    programProfile,
  };
}

/**
 * Action creator for Update Program Profile.
 * @param {IProgramProfile} programProfile the program profile to update.
 * @returns {IUpdateProgramProfile} the update program profile action.
 */
export function updateProgramProfile(programProfile: IProgramProfile): UserProfileActionTypes {
  return {
    type: USERPROFILE_UPDATE_PROGRAM_PROFILE,
    programProfile,
  };
}

/**
 * Action creator for UPDATE_LISTING_CURRENT_PAGE.
 */
export function updateCurrentPage(currentPage: number): UserProfileActionTypes {
  return {
    type: UPDATE_LISTING_CURRENT_PAGE,
    currentPage,
  };
}

/**
 * Action creator for UPDATE_LISTING_PAGE_SIZE.
 */
export function updatePageSize(pageSize: number): UserProfileActionTypes {
  return {
    type: UPDATE_LISTING_PAGE_SIZE,
    pageSize,
  };
}

/**
 * Action creator for USERPROFILES_SEARCH.
 */
export function searchUserProfiles(
  searchText: string,
  selectedUserProfilesFilterValues: IFilterValue[],
  searchPage: number,
  pageSize: number
): UserProfileActionTypes {
  return {
    type: USERPROFILES_SEARCH,
    searchText,
    selectedUserProfilesFilterValues,
    searchPage,
    pageSize,
  };
}

/**
 * Action creator for USERPROFILES_SEARCH_SUCCESS.
 * @param userProfiles the user profiles successfully searched.
 */
export function searchUserProfilesSuccess(
  userProfiles: IUserProfile[],
  searchPage: number,
  pageSize: number,
  filteredCount: number
): UserProfileActionTypes {
  return {
    type: USERPROFILES_SEARCH_SUCCESS,
    userProfiles,
    searchPage,
    filteredCount,
    pageSize,
  };
}

/**
 * Action creator for USERPROFILES_SEARCH_FAIL.
 * @param error the error message from the API.
 */
export function searchUserProfilesFail(error: string): UserProfileActionTypes {
  return {
    type: USERPROFILES_SEARCH_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILES_FILTER_VALUE_ADD.
 * @param error the error message from the API.
 */
export function addUserProfilesFilterValue(filterValue: IFilterValue): UserProfileActionTypes {
  return {
    type: USERPROFILES_FILTER_VALUE_ADD,
    filterValue,
  };
}

/**
 * Action creator for USERPROFILES_FILTER_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeUserProfilesFilterValue(filterValue: IFilterValue): UserProfileActionTypes {
  return {
    type: USERPROFILES_FILTER_VALUE_REMOVE,
    filterValue,
  };
}

/**
 * Action creator for USERPROFILES_FILTER_PROVINCE_VALUE_REMOVE.
 */
export function removeUserProfilesFilterProvinceValue(): UserProfileActionTypes {
  return {
    type: USERPROFILES_FILTER_PROVINCE_VALUE_REMOVE,
  };
}

/**
 * Action creator for USERPROFILES_FILTER_VALUE_UPDATE.
 * @param error the error message from the API.
 */
export function updateUserProfilesFilterValue(filterValue: IFilterValue): UserProfileActionTypes {
  return {
    type: USERPROFILES_FILTER_VALUE_UPDATE,
    filterValue,
  };
}

/**
 * Action creator for USERPROFILE_LOAD.
 * @param userProfileId the id of the user profile to load.
 */
export function loadUserProfile(userProfileId: number): UserProfileActionTypes {
  return {
    type: USERPROFILE_LOAD,
    userProfileId,
  };
}

/**
 * Action creator for USERPROFILE_LOAD_BY_EMAIL.
 * @param {string} email the id of the user profile to load.
 * @returns {UserProfileActionTypes} The action type.
 */
export function loadUserProfileByEmail(email: string): UserProfileActionTypes {
  return {
    type: USERPROFILE_LOAD_BY_EMAIL,
    email,
  };
}

/**
 * Action creator for USERPROFILE_LOAD_SUCCESS.
 * @param userProfile the user profile successfully loaded.
 */
export function loadUserProfileSuccess(userProfile: IUserProfile): UserProfileActionTypes {
  return {
    type: USERPROFILE_LOAD_SUCCESS,
    userProfile,
  };
}

/**
 * Action creator for USERPROFILE_LOAD_FAIL.
 * @param error the error message from the API.
 */
export function loadUserProfileFail(error: string): UserProfileActionTypes {
  return {
    type: USERPROFILE_LOAD_FAIL,
    error,
  };
}

/**
 * Action creator for UPDATE_USERPROFILE_ID.
 */
export function updateSelectedUserProfileId(userProfileId: number): UserProfileActionTypes {
  return {
    type: UPDATE_USERPROFILE_ID,
    userProfileId,
  };
}

/**
 * Action creator for UPDATE_USERPROFILE.
 */
export function updateUserProfile(userProfile: IUserProfile): UserProfileActionTypes {
  return {
    type: USERPROFILE_UPDATE,
    userProfile,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_SUCCESS.
 * @param userProfile the user profile successfully updated.
 */
export function updateUserProfileSuccess(userProfile: IUserProfile): UserProfileActionTypes {
  return {
    type: USERPROFILE_UPDATE_SUCCESS,
    userProfile,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_COMMENTS_FAIL.
 * @param error the error message from the API.
 */
export function updateUserProfileFail(error: string): UserProfileActionTypes {
  return {
    type: USERPROFILE_UPDATE_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILE_LOAD_COMMENTS.
 */
export function loadUserProfileComments(userProfileId: number): UserProfileActionTypes {
  return {
    type: USERPROFILE_LOAD_COMMENTS,
    userProfileId,
  };
}

/**
 * Action creator for USERPROFILE_LOAD_COMMENTS_SUCCESS.
 * @param userProfileComments the user profile comment successfully loaded.
 */
export function loadUserProfileCommentsSuccess(
  userProfileComments: IUserProfileComment[]
): UserProfileActionTypes {
  return {
    type: USERPROFILE_LOAD_COMMENTS_SUCCESS,
    userProfileComments,
  };
}

/**
 * Action creator for USERPROFILE_LOAD_COMMENTS_FAIL.
 * @param error the error message from the API.
 */
export function loadUserProfileCommentsFail(error: string): UserProfileActionTypes {
  return {
    type: USERPROFILE_LOAD_COMMENTS_FAIL,
    loadCommentsError: error,
  };
}

/**
 * Action creator for USERPROFILE_ADD_COMMENT.
 * @param userProfileId The user profile id.
 * @param newComment The new comment.
 * @param createdBy The user who created the comment.
 */
export function addNewComment(
  userProfileId: number,
  newComment: string,
  createdBy: string
): UserProfileActionTypes {
  return {
    type: USERPROFILE_ADD_COMMENT,
    userProfileId,
    newComment,
    createdBy,
  };
}

/**
 * Action creator for USERPROFILE_ADD_COMMENT_SUCCESS.
 * @param userProfileId The user profile id.
 */
export function addNewCommentSuccess(userProfileId: number): UserProfileActionTypes {
  return {
    type: USERPROFILE_ADD_COMMENT_SUCCESS,
    userProfileId,
  };
}

/**
 * Action creator for USERPROFILE_ADD_COMMENT_FAIL.
 * @param errorMessage The error message.
 */
export function addNewCommentFail(errorMessage: string): UserProfileActionTypes {
  return {
    type: USERPROFILE_ADD_COMMENT_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_COMMENT.
 */
export function updateUserProfileComment(
  userProfileComment: IUserProfileComment
): UserProfileActionTypes {
  return {
    type: USERPROFILE_UPDATE_COMMENT,
    userProfileComment,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_COMMENT_SUCCESS.
 * @param userProfileComment the user profile comment successfully updated.
 */
export function updateUserProfileCommentSuccess(
  userProfileComment: IUserProfileComment
): UserProfileActionTypes {
  return {
    type: USERPROFILE_UPDATE_COMMENT_SUCCESS,
    userProfileComment,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_COMMENT_FAIL.
 * @param error the error message from the API.
 */
export function updateUserProfileCommentFail(error: string): UserProfileActionTypes {
  return {
    type: USERPROFILE_UPDATE_COMMENT_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILE_LOAD_VSBENEFITS.
 */
export function loadUserProfileVSBenefit(userProfileId: number): UserProfileActionTypes {
  return {
    type: USERPROFILE_LOAD_VSBENEFITS,
    userProfileId,
  };
}

/**
 * Action creator for USERPROFILE_LOAD_VSBENEFITS_SUCCESS.
 * @param userProfileVSBenefits the user profile benefits successfully loaded.
 */
export function loadUserProfileVSBenefitSuccess(
  userProfileVSBenefit: IUserProfileVSBenefit
): UserProfileActionTypes {
  return {
    type: USERPROFILE_LOAD_VSBENEFITS_SUCCESS,
    userProfileVSBenefit,
  };
}

/**
 * Action creator for USERPROFILE_LOAD_VSBENEFITS_FAIL.
 * @param loadVSBenefitsError the error message from the API.
 */
export function loadUserProfileVSBenefitFail(error: string): UserProfileActionTypes {
  return {
    type: USERPROFILE_LOAD_VSBENEFITS_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILE_PROVISION_VSBENEFITS.
 */
export function provisionUserProfileVSBenefit(userProfileId: number): UserProfileActionTypes {
  return {
    type: USERPROFILE_PROVISION_VSBENEFITS,
    userProfileId,
  };
}

/**
 * Action creator for USERPROFILE_PROVISION_VSBENEFITS_SUCCESS.
 * @param userProfileVSBenefitRequest the user profile benefits successfully loaded.
 */
export function provisionUserProfileVSBenefitSuccess(
  userProfileVSBenefitRequest: IUserProfileVSBenefitsUpdateRequest
): UserProfileActionTypes {
  return {
    type: USERPROFILE_PROVISION_VSBENEFITS_SUCCESS,
    userProfileVSBenefitRequest,
  };
}

/**
 * Action creator for USERPROFILE_PROVISION_VSBENEFITS_FAIL.
 * @param error the error message from the API.
 */
export function provisionUserProfileVSBenefitFail(error: string): UserProfileActionTypes {
  return {
    type: USERPROFILE_PROVISION_VSBENEFITS_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILE_EXTEND_VSBENEFITS.
 */
export function extendUserProfileVSBenefit(userProfileId: number): UserProfileActionTypes {
  return {
    type: USERPROFILE_EXTEND_VSBENEFITS,
    userProfileId,
  };
}

/**
 * Action creator for USERPROFILE_EXTEND_VSBENEFITS_SUCCESS.
 * @param userProfileComments the user profile benefits successfully loaded.
 */
export function extendUserProfileVSBenefitSuccess(
  userProfileVSBenefitRequest: IUserProfileVSBenefitsUpdateRequest
): UserProfileActionTypes {
  return {
    type: USERPROFILE_EXTEND_VSBENEFITS_SUCCESS,
    userProfileVSBenefitRequest,
  };
}

/**
 * Action creator for USERPROFILE_EXTEND_VSBENEFITS_FAIL.
 * @param error the error message from the API.
 */
export function extendUserProfileVSBenefitFail(error: string): UserProfileActionTypes {
  return {
    type: USERPROFILE_EXTEND_VSBENEFITS_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILE_END_VSBENEFITS.
 */
export function endUserProfilVSBenefit(userProfileId: number): UserProfileActionTypes {
  return {
    type: USERPROFILE_END_VSBENEFITS,
    userProfileId,
  };
}

/**
 * Action creator for USERPROFILE_END_VSBENEFITS_SUCCESS.
 * @param   userProfileVSBenefitRequest: IUserProfileVSBenefitsUpdateRequest
 the user profile benefits successfully loaded.
 */
export function endUserProfileVSBenefitSuccess(
  userProfileVSBenefitRequest: IUserProfileVSBenefitsUpdateRequest
): UserProfileActionTypes {
  return {
    type: USERPROFILE_END_VSBENEFITS_SUCCESS,
    userProfileVSBenefitRequest,
  };
}

/**
 * Action creator for USERPROFILE_END_VSBENEFITS_FAIL.
 * @param error the error message from the API.
 */
export function endUserProfileVSBenefitFail(error: string): UserProfileActionTypes {
  return {
    type: USERPROFILE_EXTEND_VSBENEFITS_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILE_VSBENEFITS_RESET.
 * @returns {IUserProfileVSBenefitsReset}
 */
export function userProfileVSBenefitReset(): IUserProfileVSBenefitsReset {
  return {
    type: USERPROFILE_VSBENEFITS_RESET,
  };
}

/**
 * Action creator for USERPROFILE_CLAIM_BENEFIT.
 * @param {string} benefitType The selected benefit type to claim.
 * @param {number} userProfileId The user profile id to claim the benefit for.
 * @param {string} tenant The tenant to claim the benefit for.
 * @returns {UserProfileActionTypes}
 */
export function claimBenefit(
  benefitType: string,
  userProfileId: number,
  tenant: string = TenantTypesEnum.MLSA
): IClaimBenefit {
  return {
    type: USERPROFILE_CLAIM_BENEFIT,
    benefitType,
    userProfileId,
    tenant,
  };
}

/**
 * Action creator for USERPROFILE_CLAIM_BENEFIT_SUCCESS.
 * @returns {UserProfileActionTypes}
 */
export function claimBenefitSuccess(): UserProfileActionTypes {
  return {
    type: USERPROFILE_CLAIM_BENEFIT_SUCCESS,
  };
}

/**
 * Action creator for USERPROFILE_CLAIM_BENEFIT_FAIL.
 * @param {string} error The error message from the API.
 * @returns {UserProfileActionTypes}
 */
export function claimBenefitFail(error: string): UserProfileActionTypes {
  return {
    type: USERPROFILE_CLAIM_BENEFIT_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILE_CLAIM_BENEFIT_RESET.
 * @returns {IClaimBenefitReset}
 */
export function claimBenefitReset(): IClaimBenefitReset {
  return {
    type: USERPROFILE_CLAIM_BENEFIT_RESET,
  };
}

/**
 * Action creator for USERPROFILE_FETCH_PROGRAM_PROFILE.
 * @param programProfileId The program profile id.
 */
export function fetchProgramProfile(programProfileId: number): IFetchProgramProfile {
  return {
    type: USERPROFILE_FETCH_PROGRAM_PROFILE,
    programProfileId,
  };
}

/**
 * Action creator for USERPROFILE_FETCH_PROGRAM_PROFILE_SUCCESS.
 * @param userProfile The user profile.
 */
export function fetchProgramProfileSuccess(
  programProfile: IProgramProfile
): IFetchProgramProfileSuccess {
  return {
    type: USERPROFILE_FETCH_PROGRAM_PROFILE_SUCCESS,
    programProfile,
  };
}

/**
 * Action creator for USERPROFILE_FETCH_PROGRAM_PROFILE_FAIL.
 * @param errorMessage The error message.
 */
export function fetchProgramProfileFail(errorMessage: string): IFetchProgramProfileFail {
  return {
    type: USERPROFILE_FETCH_PROGRAM_PROFILE_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_BLOCK_SHIPPING.
 * @returns {IUserProfileUpdateBlockShipping}
 */
export function userProfileUpdateBlockShipping(
  isShippingBlocked: boolean,
  userProfileId: number
): IUserProfileUpdateBlockShipping {
  return {
    type: USERPROFILE_UPDATE_BLOCK_SHIPPING,
    userProfileId,
    isShippingBlocked,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_BLOCK_SHIPPING_FAIL.
 * @returns {IUserProfileUpdateBlockShippingFail}
 */
export function userProfileUpdateBlockShippingFail(
  errorMessage: string
): IUserProfileUpdateBlockShippingFail {
  return {
    type: USERPROFILE_UPDATE_BLOCK_SHIPPING_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_BLOCK_SHIPPING_SUCCESS.
 * @returns {IUserProfileUpdateBlockShippingSuccess}
 */
export function userProfileUpdateBlockShippingSuccess(
  userProfileId: number
): IUserProfileUpdateBlockShippingSuccess {
  return {
    type: USERPROFILE_UPDATE_BLOCK_SHIPPING_SUCCESS,
    userProfileId,
  };
}

/**
 * Action creator for USERPROFILE_DL_LOAD.
 * @returns {IUserProfileDlLoad}
 */
export function userProfileDlLoad(userProfileId: number, tenant: string): IUserProfileDlLoad {
  return {
    type: USERPROFILE_DL_LOAD,
    userProfileId,
    tenant,
  };
}

/**
 * Action creator for USERPROFILE_DL_LOAD_SUCCESS.
 * @returns {IUserProfileDlLoadSuccess}
 */
export function userProfileDlLoadSuccess(distributionLists: any): IUserProfileDlLoadSuccess {
  return {
    type: USERPROFILE_DL_LOAD_SUCCESS,
    distributionLists,
  };
}

/**
 * Action creator for USERPROFILE_DL_LOAD_FAIL.
 * @returns {IUserProfileDlLoadFail}
 */
export function userProfileDlLoadFail(error: string): IUserProfileDlLoadFail {
  return {
    type: USERPROFILE_DL_LOAD_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILE_DL_REMOVE.
 * @returns {IUserProfileDlRemove}
 */
export function userProfileDlRemove(
  dlGroup: string,
  userEmail: string,
  tenant: string
): IUserProfileDlRemove {
  return {
    type: USERPROFILE_DL_REMOVE,
    dlGroup,
    userEmail,
    tenant,
  };
}

/**
 * Action creator for USERPROFILE_DL_REMOVE_SUCCESS.
 * @returns {IUserProfileDlRemove}
 */
export function userProfileDlRemoveSuccess(): IUserProfileDlRemoveSuccess {
  return {
    type: USERPROFILE_DL_REMOVE_SUCCESS,
  };
}

/**
 * Action creator for USERPROFILE_DL_REMOVE_SUCCESS.
 * @returns {IUserProfileDlRemoveFail}
 */
export function userProfileDlRemoveFail(error: string): IUserProfileDlRemoveFail {
  return {
    type: USERPROFILE_DL_REMOVE_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILE_DL_REMOVE_ALL.
 * @returns {IUserProfileDlRemoveAll}
 */
export function userProfileDlRemoveAll(
  userProfileId: number,
  tenant: string
): IUserProfileDlRemoveAll {
  return {
    type: USERPROFILE_DL_REMOVE_ALL,
    userProfileId,
    tenant,
  };
}

/**
 * Action creator for USERPROFILE_DL_REMOVE_ALL_SUCCESS.
 * @returns {IUserProfileDlRemoveAll}
 */
export function userProfileDlRemoveAllSuccess(): IUserProfileDlRemoveAllSuccess {
  return {
    type: USERPROFILE_DL_REMOVE_ALL_SUCCESS,
  };
}

/**
 * Action creator for USERPROFILE_DL_REMOVE_ALL_FAIL.
 * @returns {IUserProfileDlRemoveAllFail}
 */
export function userProfileDlRemoveAllFail(error: string): IUserProfileDlRemoveAllFail {
  return {
    type: USERPROFILE_DL_REMOVE_ALL_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_IS_PRIVATE.
 * @returns {IUserProfileIsPrivateUpdate}
 */
export function userProfileIsPrivateUpdate(
  isPrivate: boolean,
  userProfileId: number
): IUserProfileIsPrivateUpdate {
  return {
    type: USERPROFILE_UPDATE_IS_PRIVATE,
    isPrivate,
    userProfileId,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS.
 * @returns {IUserProfileIsPrivateUpdateSuccess}
 */
export function userProfileIsPrivateUpdateSuccess(): IUserProfileIsPrivateUpdateSuccess {
  return {
    type: USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_IS_PRIVATE_FAIL.
 * @returns {IUserProfileIsPrivateUpdate}
 */
export function userProfileIsPrivateUpdateFail(error: string): IUserProfileIsPrivateUpdateFail {
  return {
    type: USERPROFILE_UPDATE_IS_PRIVATE_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILES_RESET.
 * @returns {IUserProfileFilterValueReset}
 */
export function resetUserProfileFilterValue(): IUserProfileFilterValueReset {
  return {
    type: USERPROFILES_FILTER_VALUE_RESET,
  };
}

/*************** END FUNCTIONS *************** */

export type UserProfileActionTypes =
  | IUpdateCurrentPage
  | IUpdatePageSize
  | ISearchUserProfiles
  | ISearchUserProfilesSuccess
  | ISearchUserProfilesFail
  | IUserProfilesFilterValueAdd
  | IUserProfilesFilterValueRemove
  | IUserProfilesFilterProvinceValueRemove
  | IUserProfilesFilterValueUpdate
  | ILoadUserProfile
  | ILoadUserProfileByEmail
  | ILoadUserProfileSuccess
  | ILoadUserProfileFail
  | IUpdateUserProfileId
  | IUpdateUserProfile
  | IUpdateUserProfileSuccess
  | IUpdateUserProfileFail
  | ILoadUserProfileComments
  | ILoadUserProfileCommentsSuccess
  | ILoadUserProfileCommentsFail
  | IUpdateUserProfileComment
  | IUpdateUserProfileCommentSuccess
  | IUpdateUserProfileCommentFail
  | ILoadUserProfileVSBenefits
  | ILoadUserProfileVSBenefitsSuccess
  | ILoadUserProfileVSBenefitsFail
  | IProvisionUserProfileVSBenefits
  | IProvisionUserProfileVSBenefitsSuccess
  | IProvisionUserProfileVSBenefitsFail
  | IExtendUserProfileVSBenefits
  | IExtendUserProfileVSBenefitsSuccess
  | IExtendUserProfileVSBenefitsFail
  | IEndUserProfileVSBenefits
  | IEndUserProfileVSBenefitsSuccess
  | IEndUserProfileVSBenefitsFail
  | IUserProfileVSBenefitsReset
  | IUpdateProgramProfile
  | IUpdateProgramProfileSuccess
  | IUpdateProgramProfileFailure
  | IClaimBenefit
  | IClaimBenefitSuccess
  | IClaimBenefitFail
  | IClaimBenefitReset
  | IGetProfileBenefits
  | IGetProfileBenefitsFail
  | IGetProfileBenefitsSuccess
  | IFetchProgramProfile
  | IFetchProgramProfileFail
  | IFetchProgramProfileSuccess
  | IAddNewComment
  | IAddNewCommentFail
  | IAddNewCommentSuccess
  | IUserProfileUpdateBlockShipping
  | IUserProfileUpdateBlockShippingFail
  | IUserProfileUpdateBlockShippingSuccess
  | IUserProfileDlLoad
  | IUserProfileDlLoadSuccess
  | IUserProfileDlLoadFail
  | IUserProfileDlRemove
  | IUserProfileDlRemoveSuccess
  | IUserProfileDlRemoveFail
  | IUserProfileDlRemoveAll
  | IUserProfileDlRemoveAllSuccess
  | IUserProfileDlRemoveAllFail
  | IUserProfileIsPrivateUpdate
  | IUserProfileIsPrivateUpdateSuccess
  | IUserProfileIsPrivateUpdateFail
  | IUserProfileFilterValueReset;
